<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import ConversationSearchSelect from './ConversationSearchSelect.vue';

const emit = defineEmits(['close', 'created']);

const { t } = useI18n();
const store = useStore();
const dialogRef = ref(null);

const conversationId = ref('');
const subject = ref('');
const description = ref('');
const priority = ref('medium');
const isSubmitting = ref(false);

const priorityOptions = computed(() => [
  { value: 'low', label: t('SUPPORT_TICKETS.PRIORITY_OPTIONS.LOW') },
  { value: 'medium', label: t('SUPPORT_TICKETS.PRIORITY_OPTIONS.MEDIUM') },
  { value: 'high', label: t('SUPPORT_TICKETS.PRIORITY_OPTIONS.HIGH') },
  { value: 'urgent', label: t('SUPPORT_TICKETS.PRIORITY_OPTIONS.URGENT') },
]);

const canSubmit = computed(
  () =>
    conversationId.value &&
    subject.value.trim() &&
    priority.value &&
    !isSubmitting.value
);

const closeDialog = () => {
  dialogRef.value?.close();
};

const submit = async () => {
  if (!canSubmit.value) return;

  isSubmitting.value = true;
  try {
    const ticket = await store.dispatch('supportTickets/createSupportTicket', {
      conversation_id: conversationId.value,
      subject: subject.value.trim(),
      description: description.value.trim() || undefined,
      priority: priority.value,
    });
    closeDialog();
    emit('created', ticket);
  } catch {
    useAlert(t('SUPPORT_TICKETS.CREATE_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  dialogRef.value?.open();
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :show-cancel-button="false"
    :show-confirm-button="false"
    width="lg"
    @close="emit('close')"
  >
    <div class="relative flex flex-col gap-4 min-w-[28rem]">
      <div class="flex items-start justify-between gap-3">
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('SUPPORT_TICKETS.CREATE_TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('SUPPORT_TICKETS.CREATE_SUBTITLE') }}
          </p>
        </div>
        <NextButton
          icon="i-lucide-x"
          sm
          ghost
          slate
          class="flex-shrink-0 -mt-1"
          @click="closeDialog"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('SUPPORT_TICKETS.CONVERSATION') }}
          <span class="text-n-ruby-9">*</span>
        </label>
        <ConversationSearchSelect v-model="conversationId" />
      </div>

      <input
        v-model="subject"
        type="text"
        class="w-full px-3 py-2.5 text-sm border rounded-lg border-n-weak bg-n-solid-2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
        :placeholder="t('SUPPORT_TICKETS.TITLE_PLACEHOLDER')"
      />

      <textarea
        v-model="description"
        rows="4"
        class="w-full px-3 py-2.5 text-sm border rounded-lg resize-none border-n-weak bg-n-solid-2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
        :placeholder="t('SUPPORT_TICKETS.DESCRIPTION_PLACEHOLDER')"
      />

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('SUPPORT_TICKETS.PRIORITY') }}
          <span class="text-n-ruby-9">*</span>
        </label>
        <ComboBox
          v-model="priority"
          :options="priorityOptions"
          :placeholder="t('SUPPORT_TICKETS.PRIORITY_OPTIONS.MEDIUM')"
        />
      </div>

      <div class="flex justify-end gap-2 pt-2">
        <NextButton
          faded
          slate
          :label="t('DIALOG.BUTTONS.CANCEL')"
          @click="closeDialog"
        />
        <NextButton
          :label="t('SUPPORT_TICKETS.CREATE')"
          :disabled="!canSubmit"
          :is-loading="isSubmitting"
          @click="submit"
        />
      </div>
    </div>
    <template #footer />
  </Dialog>
</template>
